import { useCallback, useEffect, useRef, useState } from 'react'

import type { AuthSession } from 'aws-amplify/auth'
import { fetchAuthSession } from 'aws-amplify/auth'
import axios, { AxiosHeaders } from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { setUserInfo, setUserLoading } from 'redux/UserInfo/UserInfoSlice'
import UserService from 'services/user/UserService'
import { useTelemetry } from '../data/useTelemetry'
import type { RootState } from 'redux/store'
import { logger } from 'core/utils/logger'
import { useAuthenticator } from '@aws-amplify/ui-react'
import { getCognitoSubFromSession } from 'core/utils/cognito-sub'

import { analyticsService, datadogService } from 'lib/insights'

/**
 * Module-level variables to ensure session is only fetched once and shared across all hook instances
 * This prevents multiple components from each fetching the session independently
 */
let globalInitialFetchCompleted = false
let globalSession: AuthSession | null = null
let globalIsAuthReady = false

/**
 * Centralized authentication hook - SINGLE SOURCE OF TRUTH for session management
 *
 * Usage:
 * - Components should use `session` from this hook, not call fetchAuthSession directly
 * - If force refresh needed, call fetchAuthSession but MUST update via setSession
 * - Never manually set axios headers - the interceptor handles all auth headers
 */
export const useAuth = () => {
  const { identify } = useTelemetry()

  const { authStatus } = useAuthenticator((context) => [context.authStatus])
  // Initialize from global state to ensure all instances share the same session
  const [session, setSessionState] = useState<AuthSession | null>(globalSession)
  const [isAuthReady, setIsAuthReady] = useState(globalIsAuthReady)
  const sessionRef = useRef(session)
  const userService = new UserService()
  const dispatch = useDispatch()
  const userInfo = useSelector((state: RootState) => state.userInfo.userInfo)

  // Wrapper to update both local and global session state
  const setSession = useCallback((newSession: AuthSession | null) => {
    globalSession = newSession
    setSessionState(newSession)
  }, [])

  useEffect(() => {
    sessionRef.current = session
  }, [session])

  const fetchUserInfo = useCallback(
    async (user: any, isSignUp: boolean = false) => {
      // Skip if user info is already available in Redux
      if (userInfo) {
        return
      }

      try {
        if (user || isSignUp) {
          dispatch(setUserLoading(true))
          const data = await userService.getUserInfo()

          if (data) {
            dispatch(setUserInfo(data))

            const userProperties = {
              email: data.email,
              name: data.name,
              company: data.company,
              position: data.position,
              user_tier: data.user_tier,
              workspace_strid: data.workspace_strid,
            }

            // Main platform will handle alias() after redirect
            // Auth platform only needs to identify the user
            const cognitoSub = getCognitoSubFromSession(sessionRef.current)
            const mixpanelCanonicalId = cognitoSub || data.strid

            identify(mixpanelCanonicalId, {
              ...userProperties,
              tenant_user_id: data.strid,
              mixpanel_canonical_id: mixpanelCanonicalId,
              mixpanel_id_source: cognitoSub ? 'cognito_sub' : 'tenant_user_id',
            })

            if (cognitoSub) {
              // Mixpanel V2: identify with cognito_sub only; do not attach PII to people profiles.
              analyticsService.identifyOrSetProperties(cognitoSub, {})
            } else {
              console.warn(
                '[MIXPANEL][V2] Missing cognito_sub in auth app. Skipping V2 identify.'
              )
            }

            // Identify user in Datadog
            if (datadogService.isInitialized()) {
              datadogService.identify({
                id: data.strid,
                email: data.email,
                name: data.name,
                properties: {
                  user_tier: data.user_tier,
                  language: data.language,
                  workspace_strid: data.workspace_strid,
                },
              })

              // Set global context for all events
              datadogService.setGlobalContext('user_strid', data.strid)
              datadogService.setGlobalContext('user_tier', data.user_tier)

              console.log('[Datadog] User identified in useAuth:', data.strid)
            }
          } else {
            dispatch(setUserLoading(false))
          }
        }
      } catch (err) {
        console.error('Failed to fetch user info', err)
        dispatch(setUserLoading(false))
      }
    },
    [dispatch, userService, userInfo, identify]
  )

  // Set up axios interceptor once when the hook is mounted
  // This interceptor works with both localStorage and cookie-based auth
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      async (config) => {
        try {
          if (!config.headers) {
            config.headers = new AxiosHeaders()
          }

          // If we have a session, check token expiration and set header
          // With cookie storage, tokens are automatically available
          if (sessionRef.current) {
            const expiration = sessionRef.current.tokens?.idToken?.payload?.exp
            const idToken = sessionRef.current.tokens?.idToken?.toString()

            if (expiration && idToken) {
              const currentTime = Math.floor(Date.now() / 1000)
              // If token is expired, try to refresh and then set header
              if (expiration <= currentTime) {
                // fetchAuthSession will attempt to refresh tokens automatically
                const refreshedSession = await fetchAuthSession({
                  forceRefresh: true,
                })
                setSession(refreshedSession)
                const refreshedIdToken =
                  refreshedSession?.tokens?.idToken?.toString() || ''
                if (refreshedIdToken) {
                  config.headers.Authorization = `Bearer ${refreshedIdToken}`
                  config.headers['X-Tenant-Domain'] = window.location.origin
                } else {
                  // If refresh fails, sign out
                  logger.log('Failed to refresh session, signing out.')
                  // Note: This will be handled by the error boundary or calling component
                  throw new Error('Session refresh failed')
                }
              } else {
                // Token is valid, set header from current session
                config.headers.Authorization = `Bearer ${idToken}`
                config.headers['X-Tenant-Domain'] = window.location.origin
              }
            } else {
              // If no tokens in current session, throw error
              logger.log('No tokens in session.')
              throw new Error('No tokens in session')
            }
          } else {
            // If no session exists, do not add Authorization header
            logger.log('No session available for interceptor.')
            // Consider if you want to redirect to login here or handle it elsewhere
          }
        } catch (error) {
          console.error('Error in axios interceptor:', error)
          // On error, throw to be handled by calling component
          throw error
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Add response interceptor to handle tenant access errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle 403 Forbidden - likely tenant access issue
        if (error.response?.status === 403) {
          const errorMessage = error.response?.data?.message || ''
          // Check if it's a tenant-related error
          if (
            errorMessage.toLowerCase().includes('tenant') ||
            errorMessage.toLowerCase().includes('access')
          ) {
            logger.log('Tenant access denied, redirecting to main page')
            // Redirect to main page instead of going back
            window.location.href = '/'
            return Promise.reject(error)
          }
        }
        return Promise.reject(error)
      }
    )

    // Clean up the interceptors when the component unmounts
    return () => {
      axios.interceptors.request.eject(interceptor)
      axios.interceptors.response.eject(responseInterceptor)
    }
  }, [setSession]) // setSession is stable due to useCallback

  // Effect to fetch initial session when authentication status becomes authenticated
  useEffect(() => {
    const fetchInitialSession = async () => {
      // Use global flag to ensure only ONE fetch happens across all hook instances
      if (globalInitialFetchCompleted) {
        setIsAuthReady(globalIsAuthReady)
        return
      }

      try {
        if (authStatus === 'authenticated') {
          console.log('Fetching initial session from cookies...')

          // With cookie storage, Amplify automatically reads from cookies
          // No need to check localStorage - cookies handle authentication state
          const _newSession = await fetchAuthSession({ forceRefresh: false })

          // Update state only if a session was actually returned
          if (_newSession?.tokens) {
            setSession(_newSession)
            console.log('Session restored from cookies successfully')
            // Note: Axios headers are set automatically by the interceptor
            // No need to manually set them here
          } else {
            // No session found in cookies - user is not authenticated
            logger.log('No session found in cookies - user not authenticated')
          }
        }
      } catch (error: any) {
        // Check if it's an Identity Pool authentication error
        if (
          error?.name === 'NotAuthorizedException' ||
          error?.message?.includes('Unauthenticated access is not supported') ||
          error?.message?.includes('Username/client id combination not found')
        ) {
          // This is expected for unauthenticated users, just set auth ready
          logger.log('User is not authenticated, proceeding without session')
        } else {
          // Log unexpected errors
          logger.error('Error fetching initial session:', error)
        }
      } finally {
        globalInitialFetchCompleted = true
        globalIsAuthReady = true
        // Set isAuthReady to true after the initial fetch attempt, regardless of outcome
        setIsAuthReady(true)
      }
    }
    fetchInitialSession()
  }, [authStatus, setSession]) // Dependency on setSession is stable due to useCallback

  return {
    session,
    setSession,
    fetchUserInfo,
    setAxiosHeaders: useCallback(() => {}, []), // Memoize the no-op function
    isAuthReady,
  }
}
